import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const CHART_COLORS = [
    '#6366F1', // Indigo
    '#22C55E', // Green
    '#F97316', // Orange
    '#EC4899', // Pink
    '#0EA5E9', // Sky
    '#F59E0B', // Amber
    '#8B5CF6', // Purple
    '#14B8A6', // Teal
    '#EF4444', // Red
    '#10B981', // Emerald
];

const formatPercentage = (value) => Math.round(value * 1000) / 10;

const aggregateDistributionByLabel = (items, labelResolver) => {
    const buckets = new Map();

    (items ?? []).forEach((item) => {
        const resolvedLabel = labelResolver(item);
        const label = (typeof resolvedLabel === 'string' ? resolvedLabel.trim() : '') || '未設定';
        const count = Number(item?.count ?? 0);

        if (buckets.has(label)) {
            buckets.get(label).count += count;
            return;
        }

        buckets.set(label, { label, count });
    });

    const aggregated = Array.from(buckets.values());
    const total = aggregated.reduce((sum, item) => sum + (item.count ?? 0), 0);

    return aggregated.map((item) => ({
        ...item,
        percentage: total > 0 ? formatPercentage(item.count / total) : 0
    }));
};

const toJapaneseEmploymentStatus = (status) => {
    if (!status) return '未設定';
    const trimmed = status.trim();
    const map = {
        '在職': '在職',
        '在职': '在職',
        '在職中': '在職',
        '離職': '離職',
        '离职': '離職',
        '退職': '退職',
        '内定': '内定',
        '申請中': '申請中',
        '申请中': '申請中',
        '書類申請中': '書類申請中',
        '面接中': '面接中',
        '面试中': '面接中',
        '準備中': '準備中',
        '准备中': '準備中',
        '未設定': '未設定',
        '未设置': '未設定',
        '育休': '育休',
        '待入国': '待入社'
    };
    return map[trimmed] || trimmed;
};

const toJapaneseNationality = (nationality) => {
    if (!nationality) return '未設定';
    const trimmed = nationality.trim();
    const map = {
        '中国': '中国',
        'China': '中国',
        '韓国': '韓国',
        '韩国': '韓国',
        'Korea': '韓国',
        '缅甸': 'ミャンマー',
        'ミャンマー': 'ミャンマー',
        'Myanmar': 'ミャンマー',
        '印度尼西亚': 'インドネシア',
        'インドネシア': 'インドネシア',
        'Indonesia': 'インドネシア',
        '越南': 'ベトナム',
        'ベトナム': 'ベトナム',
        'Vietnam': 'ベトナム',
        '台湾': '台湾',
        'Taiwan': '台湾',
        '日本': '日本',
        'Japan': '日本',
        '菲律宾': 'フィリピン',
        'フィリピン': 'フィリピン',
        'Philippines': 'フィリピン',
        '泰国': 'タイ',
        'タイ': 'タイ',
        'Thailand': 'タイ',
        'ラオス': 'ラオス',
        '老挝': 'ラオス'
    };
    return map[trimmed] || trimmed;
};

const toJapaneseField = (field) => {
    if (!field) return '未設定';
    const trimmed = field.trim();
    const map = {
        '外食': '外食',
        '飲食料品製造': '飲食料品製造',
        '饮食料品製造': '飲食料品製造',
        '飲食製造': '飲食料品製造',
        '溶接': '溶接',
        '介護': '介護',
        '介护': '介護',
        '介護福祉': '介護',
        '農業': '農業',
        '农业': '農業',
        '建設': '建設',
        '建设': '建設',
        'ビルメンテナンス': 'ビルメンテナンス',
        '未設定': '未設定',
        '未设置': '未設定'
    };
    return map[trimmed] || trimmed;
};

const createPieOptions = (distribution, labelAccessor, totalCountFallback = 0) => {
    const total = distribution.reduce((sum, item) => sum + (item.count ?? 0), 0) || totalCountFallback;

    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    font: { size: 11 },
                    generateLabels: function (chart) {
                        const dataset = chart.data.datasets[0];
                        const colors = Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor : [dataset.backgroundColor];

                        return distribution.map((item, index) => {
                            const value = dataset.data[index] ?? 0;
                            const color = colors[index % colors.length];
                            const label = labelAccessor(item, index, chart.data.labels?.[index] ?? '');
                            const percentage = item?.percentage ?? (total > 0 ? formatPercentage(value / total) : 0);

                            return {
                                text: `${label}: ${value}人 (${percentage}%)`,
                                fillStyle: color,
                                hidden: false,
                                index
                            };
                        });
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const value = context.parsed;
                        const item = distribution[context.dataIndex];
                        const label = labelAccessor(item, context.dataIndex, context.label || '');
                        const percentage = item?.percentage ?? (total > 0 ? formatPercentage(value / total) : 0);
                        return `${label}: ${value}人 (${percentage}%)`;
                    }
                }
            }
        }
    };
};

export default function SalesAnalytics() {
    const { t } = useTranslation();
    const [analytics, setAnalytics] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const initialDateFilter = { startMonth: '', endMonth: '' };
    const [dateFilters, setDateFilters] = useState(initialDateFilter);
    const [pendingDateFilters, setPendingDateFilters] = useState(initialDateFilter);
    const [dateValidationError, setDateValidationError] = useState(null);
    const [managementFeeView, setManagementFeeView] = useState('current');
    const [syncInitialCostFilters, setSyncInitialCostFilters] = useState(false);
    const [initialCostDateFilters, setInitialCostDateFilters] = useState(initialDateFilter);
    const [pendingInitialCostDateFilters, setPendingInitialCostDateFilters] = useState(initialDateFilter);
    const [initialCostDateValidationError, setInitialCostDateValidationError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const loadAnalytics = async () => {
            setIsLoading(true);
            try {
                const data = await apiService.getSalesAnalytics({
                    startMonth: dateFilters.startMonth || undefined,
                    endMonth: dateFilters.endMonth || undefined,
                    initialCostStartMonth: (syncInitialCostFilters ? dateFilters.startMonth : initialCostDateFilters.startMonth) || undefined,
                    initialCostEndMonth: (syncInitialCostFilters ? dateFilters.endMonth : initialCostDateFilters.endMonth) || undefined
                });
                if (!isMounted) return;
                setAnalytics(data);
                setError(null);
            } catch (err) {
                if (!isMounted) return;
                console.error('Failed to load sales analytics', err);
                setError(err?.message || t('salesAnalytics.loadError'));
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadAnalytics();

        return () => {
            isMounted = false;
        };
    }, [
        t,
        dateFilters.startMonth,
        dateFilters.endMonth,
        syncInitialCostFilters,
        initialCostDateFilters.startMonth,
        initialCostDateFilters.endMonth
    ]);

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-lg p-6 animate-pulse">
                    <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
                    <div className="h-64 bg-slate-200 rounded"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-xl shadow-lg p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('salesAnalytics.title')}</h3>
                <p className="text-sm text-red-600">{t('salesAnalytics.errorOccurred')}: {error}</p>
            </div>
        );
    }

    if (!analytics || analytics.totalCount === 0) {
        return (
            <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('salesAnalytics.title')}</h3>
                <p className="text-sm text-gray-600">{t('salesAnalytics.noData')}</p>
            </div>
        );
    }

    const employmentStatusDistribution = aggregateDistributionByLabel(
        analytics.employmentStatusDistribution,
        (item) => toJapaneseEmploymentStatus(item?.status)
    );

    const nationalityDistribution = aggregateDistributionByLabel(
        analytics.nationalityDistribution,
        (item) => toJapaneseNationality(item?.nationality)
    );

    const fieldDistribution = aggregateDistributionByLabel(
        analytics.fieldDistribution,
        (item) => toJapaneseField(item?.field)
    );

    // 1. 进度状况分布饼图
    const employmentStatusData = {
        labels: employmentStatusDistribution.map(s => s.label),
        datasets: [{
            data: employmentStatusDistribution.map(s => s.count),
            backgroundColor: CHART_COLORS,
            borderWidth: 2,
            borderColor: '#ffffff'
        }]
    };

    const employmentPieOptions = createPieOptions(
        employmentStatusDistribution,
        (item, _index, fallback) => item?.label || fallback || '未設定',
        analytics.totalCount
    );

    // 2. 国籍分布饼图
    const nationalityData = {
        labels: nationalityDistribution.map(n => n.label),
        datasets: [{
            data: nationalityDistribution.map(n => n.count),
            backgroundColor: CHART_COLORS,
            borderWidth: 2,
            borderColor: '#ffffff'
        }]
    };

    const nationalityPieOptions = createPieOptions(
        nationalityDistribution,
        (item, _index, fallback) => item?.label || fallback || '未設定',
        analytics.totalCount
    );

    // 3. 分野分布饼图
    const fieldData = {
        labels: fieldDistribution.map(f => f.label),
        datasets: [{
            data: fieldDistribution.map(f => f.count),
            backgroundColor: CHART_COLORS,
            borderWidth: 2,
            borderColor: '#ffffff'
        }]
    };

    const fieldPieOptions = createPieOptions(
        fieldDistribution,
        (item, _index, fallback) => item?.label || fallback || '未設定',
        analytics.totalCount
    );

    // 4. 月度新增统计柱状图
    const monthlyNewData = {
        labels: analytics.monthlyNewTalents.map(m => m.month),
        datasets: [{
            label: t('salesAnalytics.newTalents'),
            data: analytics.monthlyNewTalents.map(m => m.count),
            backgroundColor: '#6366F1',
            borderRadius: 0,
            barThickness: 30
        }]
    };

    const monthlyNewOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return `${context.parsed.y}人`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                    callback: function (value) {
                        return value + '人';
                    }
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    };

    // 5. 季度对比柱状图
    const quarterlyData = {
        labels: analytics.quarterlyStats.map(q => q.quarter),
        datasets: [{
            label: t('salesAnalytics.quarterlyTalents'),
            data: analytics.quarterlyStats.map(q => q.count),
            backgroundColor: ['#6366F1', '#22C55E', '#F97316', '#EC4899'],
            borderRadius: 0,
            barThickness: 50
        }]
    };

    const quarterlyOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return `${context.parsed.y}人`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                    callback: function (value) {
                        return value + '人';
                    }
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('ja-JP', {
            style: 'currency',
            currency: 'JPY',
            minimumFractionDigits: 0
        }).format(value);
    };

    const handleDateFilterChange = (field, value) => {
        setPendingDateFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleClearDateFilters = () => {
        setPendingDateFilters(initialDateFilter);
        setDateFilters(initialDateFilter);
        setDateValidationError(null);
        setManagementFeeView('current');
    };

    const handleApplyDateFilters = () => {
        const { startMonth, endMonth } = pendingDateFilters;
        if (startMonth && endMonth && startMonth > endMonth) {
            setDateValidationError(t('salesAnalytics.invalidDateRange'));
            return;
        }
        setDateValidationError(null);
        setDateFilters(pendingDateFilters);
        const hasRangeFilter = Boolean(startMonth || endMonth);
        if (hasRangeFilter && managementFeeView === 'current') {
            setManagementFeeView('rangeCombined');
        }
    };

    const handleInitialCostDateFilterChange = (field, value) => {
        setPendingInitialCostDateFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleClearInitialCostDateFilters = () => {
        setPendingInitialCostDateFilters(initialDateFilter);
        setInitialCostDateFilters(initialDateFilter);
        setInitialCostDateValidationError(null);
    };

    const handleApplyInitialCostDateFilters = () => {
        const { startMonth, endMonth } = pendingInitialCostDateFilters;
        if (startMonth && endMonth && startMonth > endMonth) {
            setInitialCostDateValidationError(t('salesAnalytics.invalidDateRange'));
            return;
        }
        setInitialCostDateValidationError(null);
        setInitialCostDateFilters(pendingInitialCostDateFilters);
    };

    const initialCostPendingFilters = syncInitialCostFilters ? pendingDateFilters : pendingInitialCostDateFilters;

    const initialCostStats = analytics.initialCostStats ?? {
        totalInitialCost: 0,
        averageInitialCost: 0,
        medianInitialCost: 0,
        maxCost: 0,
        minCost: 0,
        talentsWithCost: 0,
        coverageRate: 0,
        noChargeTalents: 0,
        chargeablePopulation: 0
    };

    const initialCostTalentCount = initialCostStats.talentsWithCost ?? 0;
    const managementFeeStats = analytics.managementFeeStats ?? {
        totalMonthlyFee: 0,
        averageFeePerTalent: 0,
        medianFee: 0,
        maxFee: 0,
        minFee: 0,
        projectedAnnualRevenue: 0,
        firstMonthFeeTotal: 0,
        firstMonthFeeCount: 0,
        talentsWithFee: 0,
        activeTalentsCount: 0,
        noChargeTalents: 0
    };
    const managementFeeViewLabels = {
        current: t('salesAnalytics.managementFeeViewOption.current'),
        rangeActual: t('salesAnalytics.managementFeeViewOption.rangeActual'),
        rangeForecast: t('salesAnalytics.managementFeeViewOption.rangeForecast'),
        rangeCombined: t('salesAnalytics.managementFeeViewOption.rangeCombined')
    };
    const fallbackManagementView = {
        startMonth: '',
        endMonth: '',
        totalFee: 0,
        averageFeePerTalent: 0,
        medianFee: 0,
        maxFee: 0,
        minFee: 0,
        talentsWithFee: 0,
        targetTalentsCount: 0,
        noChargeTalents: 0,
        billedMonths: 0,
        pausedMonths: 0
    };
    const currentManagementFeeView = managementFeeStats.views?.[managementFeeView] ?? fallbackManagementView;
    const activeTalentCount = managementFeeStats.activeTalentsCount ?? managementFeeStats.talentsWithFee ?? 0;
    const currentMonthTalentCount = managementFeeStats.currentMonth?.talentsWithFee ?? managementFeeStats.talentsWithFee ?? 0;
    const currentViewTalentCount = currentManagementFeeView.talentsWithFee ?? 0;
    const currentTargetTalentCount = currentManagementFeeView.targetTalentsCount ?? currentManagementFeeView.talentsWithFee ?? 0;
    const currentNoChargeTalentCount = currentManagementFeeView.noChargeTalents ?? managementFeeStats.noChargeTalents ?? 0;
    const currentFeeCountLabel = managementFeeView === 'current'
        ? t('salesAnalytics.currentMonthTalentsCount', { count: currentMonthTalentCount })
        : t('salesAnalytics.viewTalentsCount', {
            view: managementFeeViewLabels[managementFeeView] ?? t('salesAnalytics.managementFeeViewOption.current'),
            count: currentViewTalentCount
        });
    const talentCountLabel = managementFeeView === 'current'
        ? t('salesAnalytics.totalTalentsCount')
        : t('salesAnalytics.targetTalentsCount');
    const displayedTalentCount = managementFeeView === 'current'
        ? activeTalentCount
        : currentTargetTalentCount;
    const managementFeeTotalLabel = managementFeeView === 'current'
        ? t('salesAnalytics.totalMonthlyFee')
        : t('salesAnalytics.totalRangeFee');
    const isRangeView = managementFeeView !== 'current';
    const specifiedSkill2Stats = analytics.specifiedSkill2Stats ?? {
        totalCount: 0,
        totalIntroductionFee: 0,
        averageIntroductionFee: 0,
        unregisteredFeeCount: 0,
        bySalesStaff: []
    };

    return (
        <div className="space-y-6">
            {/* 特定技能2号統計カード */}
            <div className="bg-gradient-to-br from-cyan-50 to-indigo-50 rounded-xl shadow-lg p-6">
                <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-indigo-900">特定技能2号統計</h3>
                        <p className="text-sm text-indigo-700 mt-1">現在の担当人材のうち、特定技能2号は通常統計から分離しています。</p>
                    </div>
                    <div className="text-sm text-indigo-700">
                        未登録紹介費: {specifiedSkill2Stats.unregisteredFeeCount ?? 0}人
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">人数</div>
                        <div className="text-2xl font-bold text-indigo-600">{specifiedSkill2Stats.totalCount ?? 0}人</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">紹介費合計</div>
                        <div className="text-2xl font-bold text-cyan-700">{formatCurrency(specifiedSkill2Stats.totalIntroductionFee ?? 0)}</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">平均紹介費</div>
                        <div className="text-2xl font-bold text-emerald-600">{formatCurrency(specifiedSkill2Stats.averageIntroductionFee ?? 0)}</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">営業担当</div>
                        <div className="text-2xl font-bold text-slate-700">自分</div>
                    </div>
                </div>
            </div>

            {/* 初期費用統計カード */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl shadow-lg p-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                    <h3 className="text-lg font-semibold text-orange-900">
                        {t('salesAnalytics.initialCostStats')}
                    </h3>
                    <div className="w-full md:w-auto">
                        <div className="flex items-center gap-2 mb-2">
                            <input
                                id="initial-cost-sync-sales"
                                type="checkbox"
                                checked={syncInitialCostFilters}
                                onChange={(event) => setSyncInitialCostFilters(event.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                            />
                            <label htmlFor="initial-cost-sync-sales" className="text-xs text-gray-600">
                                {t('salesAnalytics.initialCostSyncLabel')}
                            </label>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">{t('salesAnalytics.startMonth')}</label>
                                <input
                                    type="month"
                                    value={initialCostPendingFilters.startMonth}
                                    onChange={(e) => handleInitialCostDateFilterChange('startMonth', e.target.value)}
                                    disabled={syncInitialCostFilters}
                                    className={`w-full rounded-md border border-orange-200 px-3 py-2 text-sm text-gray-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white ${syncInitialCostFilters ? 'opacity-60' : ''}`}
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">{t('salesAnalytics.endMonth')}</label>
                                <input
                                    type="month"
                                    value={initialCostPendingFilters.endMonth}
                                    onChange={(e) => handleInitialCostDateFilterChange('endMonth', e.target.value)}
                                    disabled={syncInitialCostFilters}
                                    className={`w-full rounded-md border border-orange-200 px-3 py-2 text-sm text-gray-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white ${syncInitialCostFilters ? 'opacity-60' : ''}`}
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    type="button"
                                    onClick={handleApplyInitialCostDateFilters}
                                    disabled={syncInitialCostFilters}
                                    className={`w-full rounded-md bg-orange-600 text-white px-3 py-2 text-sm hover:bg-orange-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-400 ${syncInitialCostFilters ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                    {t('salesAnalytics.applyDateFilters')}
                                </button>
                            </div>
                            <div className="flex items-end">
                                <button
                                    type="button"
                                    onClick={handleClearInitialCostDateFilters}
                                    disabled={syncInitialCostFilters}
                                    className={`w-full rounded-md border border-orange-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-400 ${syncInitialCostFilters ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                    {t('salesAnalytics.clearDateFilters')}
                                </button>
                            </div>
                        </div>
                        {!syncInitialCostFilters && initialCostDateValidationError && (
                            <p className="text-xs text-red-600 mt-2">{initialCostDateValidationError}</p>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.initialCostTalentCount')}</div>
                        <div className="text-2xl font-bold text-indigo-600">
                            {initialCostTalentCount}人
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {t('salesAnalytics.initialCostCoverageRate', { rate: initialCostStats.coverageRate ?? 0 })}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {t('salesAnalytics.initialCostChargeablePopulation', { count: initialCostStats.chargeablePopulation ?? 0 })}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {t('salesAnalytics.initialCostNoChargeCount', { count: initialCostStats.noChargeTalents ?? 0 })}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.totalInitialCost')}</div>
                        <div className="text-2xl font-bold text-orange-600">
                            {formatCurrency(initialCostStats.totalInitialCost)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.averageInitialCost')}</div>
                        <div className="text-2xl font-bold text-green-600">
                            {formatCurrency(initialCostStats.averageInitialCost)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.medianInitialCost')}</div>
                        <div className="text-2xl font-bold text-sky-600">
                            {formatCurrency(initialCostStats.medianInitialCost ?? 0)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.maxInitialCost')}</div>
                        <div className="text-2xl font-bold text-red-500">
                            {formatCurrency(initialCostStats.maxCost ?? 0)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.minInitialCost')}</div>
                        <div className="text-2xl font-bold text-sky-600">
                            {formatCurrency(initialCostStats.minCost ?? 0)}
                        </div>
                    </div>
                </div>
            </div>

            {/* 管理费统计卡片 */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-lg p-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                    <h3 className="text-lg font-semibold text-green-900">
                        {t('salesAnalytics.managementFeeStats')}
                    </h3>
                    <div className="w-full md:w-auto">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 mb-2">
                            <label htmlFor="sales-management-fee-view" className="text-xs text-gray-600 whitespace-nowrap">
                                {t('salesAnalytics.managementFeeViewLabel')}
                            </label>
                            <select
                                id="sales-management-fee-view"
                                value={managementFeeView}
                                onChange={(event) => setManagementFeeView(event.target.value)}
                                className="mt-1 sm:mt-0 rounded-md border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                            >
                                <option value="current">{t('salesAnalytics.managementFeeViewOption.current')}</option>
                                <option value="rangeActual">{t('salesAnalytics.managementFeeViewOption.rangeActual')}</option>
                                <option value="rangeForecast">{t('salesAnalytics.managementFeeViewOption.rangeForecast')}</option>
                                <option value="rangeCombined">{t('salesAnalytics.managementFeeViewOption.rangeCombined')}</option>
                            </select>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">{t('salesAnalytics.startMonth')}</label>
                                <input
                                    type="month"
                                    value={pendingDateFilters.startMonth}
                                    onChange={(e) => handleDateFilterChange('startMonth', e.target.value)}
                                    disabled={!isRangeView}
                                    className={`w-full rounded-md border border-emerald-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 ${isRangeView ? 'bg-white' : 'bg-gray-100 opacity-60 cursor-not-allowed'}`}
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">{t('salesAnalytics.endMonth')}</label>
                                <input
                                    type="month"
                                    value={pendingDateFilters.endMonth}
                                    onChange={(e) => handleDateFilterChange('endMonth', e.target.value)}
                                    disabled={!isRangeView}
                                    className={`w-full rounded-md border border-emerald-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 ${isRangeView ? 'bg-white' : 'bg-gray-100 opacity-60 cursor-not-allowed'}`}
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    type="button"
                                    onClick={handleApplyDateFilters}
                                    disabled={!isRangeView}
                                    className={`w-full rounded-md text-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 ${isRangeView ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-emerald-300 cursor-not-allowed'}`}
                                >
                                    {t('salesAnalytics.applyDateFilters')}
                                </button>
                            </div>
                            <div className="flex items-end">
                                <button
                                    type="button"
                                    onClick={handleClearDateFilters}
                                    disabled={!isRangeView}
                                    className={`w-full rounded-md border border-emerald-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 ${isRangeView ? 'bg-white hover:bg-emerald-50' : 'bg-gray-100 opacity-60 cursor-not-allowed'}`}
                                >
                                    {t('salesAnalytics.clearDateFilters')}
                                </button>
                            </div>
                        </div>
                        {!isRangeView && (
                            <p className="text-xs text-gray-500 mt-2">{t('salesAnalytics.managementFeeCurrentRangeDisabledHint')}</p>
                        )}
                        {isRangeView && dateValidationError && (
                            <p className="text-xs text-red-600 mt-2">{dateValidationError}</p>
                        )}
                    </div>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                    {t('salesAnalytics.managementFeeViewRange', {
                        start: currentManagementFeeView.startMonth || '-',
                        end: currentManagementFeeView.endMonth || '-'
                    })}
                </p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{talentCountLabel}</div>
                        <div className="text-2xl font-bold text-indigo-600">
                            {displayedTalentCount}人
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {currentFeeCountLabel}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {t('salesAnalytics.managementFeeNoChargeCount', { count: currentNoChargeTalentCount })}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{managementFeeTotalLabel}</div>
                        <div className="text-2xl font-bold text-green-600">
                            {formatCurrency(currentManagementFeeView.totalFee ?? 0)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.averageFeePerTalent')}</div>
                        <div className="text-2xl font-bold text-orange-600">
                            {formatCurrency(currentManagementFeeView.averageFeePerTalent ?? 0)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.medianFee')}</div>
                        <div className="text-2xl font-bold text-sky-600">
                            {formatCurrency(currentManagementFeeView.medianFee ?? 0)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.maxFee')}</div>
                        <div className="text-2xl font-bold text-red-500">
                            {formatCurrency(currentManagementFeeView.maxFee ?? 0)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.minFee')}</div>
                        <div className="text-2xl font-bold text-slate-600">
                            {formatCurrency(currentManagementFeeView.minFee ?? 0)}
                        </div>
                    </div>
                    {managementFeeView === 'current' ? (
                        <>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.projectedAnnualRevenue')}</div>
                                <div className="text-2xl font-bold text-purple-600">
                                    {formatCurrency(managementFeeStats.projectedAnnualRevenue)}
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.firstMonthFeeTotal')}</div>
                                <div className="text-2xl font-bold text-pink-600">
                                    {formatCurrency(managementFeeStats.firstMonthFeeTotal ?? 0)}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {t('salesAnalytics.firstMonthFeeCount', { count: managementFeeStats.firstMonthFeeCount ?? 0 })}
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.billedMonths')}</div>
                                <div className="text-2xl font-bold text-purple-600">
                                    {currentManagementFeeView.billedMonths ?? 0}{t('salesAnalytics.monthsUnit')}
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <div className="text-sm text-gray-600 mb-1">{t('salesAnalytics.pausedMonths')}</div>
                                <div className="text-2xl font-bold text-pink-600">
                                    {currentManagementFeeView.pausedMonths ?? 0}{t('salesAnalytics.monthsUnit')}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* 三个饼图 */}
            <div className="grid lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {t('salesAnalytics.employmentStatusDistribution')}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {t('salesAnalytics.employmentStatusDistributionSubtitle')}
                        </p>
                    </div>
                    <div style={{ height: '300px' }}>
                        <Pie data={employmentStatusData} options={employmentPieOptions} />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {t('salesAnalytics.nationalityDistribution')}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {t('salesAnalytics.nationalityDistributionSubtitle')}
                        </p>
                    </div>
                    <div style={{ height: '300px' }}>
                        <Pie data={nationalityData} options={nationalityPieOptions} />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {t('salesAnalytics.fieldDistribution')}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {t('salesAnalytics.fieldDistributionSubtitle')}
                        </p>
                    </div>
                    <div style={{ height: '300px' }}>
                        <Pie data={fieldData} options={fieldPieOptions} />
                    </div>
                </div>
            </div>

            {/* 月度新增和季度对比 */}
            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {t('salesAnalytics.monthlyNewTalents')}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {t('salesAnalytics.monthlyNewTalentsSubtitle')}
                        </p>
                    </div>
                    <div style={{ height: '300px' }}>
                        <Bar data={monthlyNewData} options={monthlyNewOptions} />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {t('salesAnalytics.quarterlyStats')}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {t('salesAnalytics.quarterlyStatsSubtitle', { year: new Date().getFullYear() })}
                        </p>
                    </div>
                    <div style={{ height: '300px' }}>
                        <Bar data={quarterlyData} options={quarterlyOptions} />
                    </div>
                </div>
            </div>
        </div>
    );
}
